import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@nubeteck/components";
import { UserHelp } from "../../other";
import { LogoCenfotecBlancoPNG } from "../../../assets";
import LanguageSwitcher from "../../LanguageSwitcher";
import {
  HeaderStyled,
  HomeLogo,
  MenuStyled,
  MobileMenuButtonStyled,
  UserSectionStyled,
  HeaderText,
  DropdownContainerStyled,
  DropdownLinkStyled,
} from "./header.styled";
import { IMenuItem } from "src/Interfaces/options";
import { useMediaQuery } from "src/hooks";
import { Dropdown, Space, Skeleton } from "antd";
import { MenuProps } from "antd/lib";
import { useSelector } from "react-redux";
import { RootState } from "@/core/store/store";
import { getLoginPage } from "src/Api/content";
import { useLocale } from "src/i18n/localeContext";
import { StrapiError } from "../../../pages/StrapiError";

interface IHeader {
  menuItems: (IMenuItem | null)[];
  userName: string;
  openMenu: () => void;
  isHelpVisible: boolean;
  isFakeSelecting: boolean;
  logout: () => void;
  logoutImpersonated: () => void;
}

const UserSection = ({
  userName,
  logout,
  isHelpVisible,
  isAdmin,
  availableRoles,
  isATeacher,
}: {
  userName: string;
  logout: () => void;
  isHelpVisible: boolean;
  isAdmin: boolean;
  isATeacher: boolean;
  availableRoles: MenuProps;
}) => (
  <UserSectionStyled>
    {!isAdmin && <>{/* <Notifications /> */}</>}
    {isHelpVisible && <UserHelp />}
    <div className="language-switcher">
      <LanguageSwitcher />
    </div>
    <DropdownContainerStyled>
      <HeaderText
        as="span"
        className="username"
        style={{
          maxInlineSize: "145px",
          display: "block",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
      >
        {userName}
      </HeaderText>
      <Dropdown menu={availableRoles} trigger={["hover"]}>
        <DropdownLinkStyled className="role-dropdown">
          <Space>
            {isATeacher ? "Docente" : isAdmin ? "Admin" : "Estudiante"}
            <Icon name="" />
          </Space>
        </DropdownLinkStyled>
      </Dropdown>
    </DropdownContainerStyled>
    <Icon isTouchable={true} name="power_settings_new" color="white" onClick={logout} />
  </UserSectionStyled>
);

const Header = ({
  menuItems,
  userName,
  openMenu,
  isHelpVisible,
  isFakeSelecting,
  logout,
  logoutImpersonated,
}: IHeader) => {
  const { locale } = useLocale();
  const [loginPage, setLoginPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isASmallMobile = useMediaQuery("(max-inline-size: 480px)");
  const profiles = localStorage.getItem("profiles")
    ? JSON.parse(localStorage.getItem("profiles") ?? "")
    : {};
  const { isATeacher } = useSelector((state: RootState) => state.user);
  const { isAdmin } = useSelector((state: RootState) => state.user);
  const [hasError, setHasError] = useState<boolean>(false);
  const rolesItems: MenuProps["items"] = Object?.keys(profiles)?.map((pro) => ({
    key: pro,
    label: pro,
    onClick: () => {
      localStorage.setItem("token", profiles[pro]);
      navigate("/");
      window.location.reload();
    },
  }));

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setLoginPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getLoginPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setLoginPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setLoginPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });

    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setLoginPage(null);
      }}
    />;
  }

  return (
    <HeaderStyled title="Cenfotec Autoservicio">
      <HomeLogo
        src={LogoCenfotecBlancoPNG}
        width={75}
        preview={false}
        style={{ cursor: "pointer" }}
        onClick={() => {
          navigate("/");
        }}
      />
      <HeaderText>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (loginPage?.name || "AUTOSERVICIO")} {isATeacher && !isASmallMobile ? "DOCENTES" : null}</HeaderText>
      <MenuStyled>
        {menuItems
          .filter((item): item is IMenuItem => item !== null)
          .map((item, index) => (
            <li key={`${item.name}-${index}`}>
              <NavLink
                title={item.tooltip}
                onClick={
                  isFakeSelecting
                    ? () => {
                        logoutImpersonated();
                        navigate(item.link);
                        window.location.reload();
                      }
                    : () => null
                }
                to={item.link}
                end
                className={
                  item?.exactLink
                    ? location.pathname.split("/")[1] === item.exactLink
                      ? "is-active"
                      : ""
                    : ({ isActive }) => (isActive ? "is-active" : "")
                }
              >
                {item.name}
              </NavLink>
            </li>
          ))}
      </MenuStyled>
      <UserSection
        userName={userName}
        logout={logout}
        isHelpVisible={isHelpVisible}
        isAdmin={isAdmin}
        isATeacher={isATeacher}
        availableRoles={{ items: rolesItems }}
      />
      <MobileMenuButtonStyled type="text" onClick={openMenu}>
        <Icon name="" size={32} />
      </MobileMenuButtonStyled>
    </HeaderStyled>
  );
};

export default Header;
